import Axios from "../utils/axiosInterceptor";
import { CLAIM_LIST, PATIENT_APIS } from "../utils/constants";
import { StatusCodes } from "http-status-codes";
import type { IClaimDownload, IClaimFilter } from "../interfaces/IClaim";
import {
  buildSaveFormData,
  type BpaDocumentDto,
  type BpaDocumentMeta,
  type BpaStageStatus,
  type StagedFile,
  type StageActionRequest,
} from "./dischargeService";

/** Legacy IHX claims list (ClaimsListContainer / claimSlice) */
export const fetchClaimData = async (
  filters: IClaimFilter,
  pageNumber: number,
  pageSize: number,
) => {
  const response = await Axios.post(
    `${CLAIM_LIST.CLAIM_lIST_DOWNLOAD}?pageNumber=${pageNumber}&pageSize=${pageSize}`,
    filters,
    { headers: { "Content-Type": "application/json" } },
  );
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Cannot fetch Claim details");
};

export const fetchClaimDownloadData = async (body: IClaimDownload) => {
  const response = await Axios.post(CLAIM_LIST.CLAIM_lIST_DOWNLOAD, body, {
    headers: { "Content-Type": "application/json" },
  });
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Cannot fetch data");
};

export type ClaimProcedureRequest = {
  procedure?: string;
  procedureCode?: string;
  quantity?: string;
  accreditationStatus?: string;
  treatingDoctor?: string;
  treatmentStartDate?: string;
  procedureAmount?: string;
  unspecifiedProcedureName?: string;
};

export type SaveClaimRequest = {
  additionalAmountPaid?: boolean;
  amount?: string;
  totalBillAmount?: string;
  totalClaimAmount?: string;
  billNumber?: string;
  billDate?: string;
  billAmount?: string;
  drugAmount?: string;
  consumableAmount?: string;
  postDischargeMedicineAmount?: string;
  implantAmount?: string;
  unlistedTotal?: string;
  remarks?: string;
  procedures?: ClaimProcedureRequest[];
  saveAsDraft?: boolean;
  documents?: BpaDocumentMeta[];
};

export type WorkflowReviewDto = {
  workflowStage: string;
  action?: string;
  amount?: string;
  remarks?: string;
  reviewerName?: string;
  stageStatus?: BpaStageStatus;
};

export type ClaimResponse = {
  id?: number;
  visitId: number;
  caseId?: number;
  saveAsDraft?: boolean;
  initiated?: boolean;
  submitted?: boolean;
  approved?: boolean;
  additionalAmountPaid?: boolean;
  amount?: string;
  totalBillAmount?: string;
  totalClaimAmount?: string;
  billNumber?: string;
  billDate?: string;
  billAmount?: string;
  drugAmount?: string;
  consumableAmount?: string;
  postDischargeMedicineAmount?: string;
  implantAmount?: string;
  unlistedTotal?: string;
  remarks?: string;
  workflowStatus?: string;
  stageStatus?: BpaStageStatus;
  settlementStageStatus?: BpaStageStatus;
  workflowStageStatuses?: Record<string, BpaStageStatus>;
  currentWorkflowStage?: string;
  workflowPermissions?: Record<string, boolean>;
  procedures?: ClaimProcedureRequest[];
  documents?: BpaDocumentDto[];
  workflowReviews?: WorkflowReviewDto[];
  enhancementAwaitingApproval?: boolean;
  claimReviewBlockedByEnhancement?: boolean;
  caseOverallStatus?: string | null;
  enhancementClaimGate?: "NONE" | "SUBMIT_BLOCKED" | "LOCKED" | string;
  enhancementClaimGateMessage?: string | null;
  intimatedAmount?: string | null;
  approvedAmount?: string | null;
  netSettledAmount?: string | null;
  grossSettledAmount?: string | null;
  settlementId?: string | null;
  tds?: string | null;
  tax?: string | null;
  settledDate?: string | null;
};

export type WorkflowActionRequest = {
  action: "APPROVE" | "REJECT" | "QUERY" | "SUBMIT";
  amount?: string;
  remarks?: string;
  /** QUERY only: records seek-opinion timeline instead of deferring to query management. */
  seekExpertOpinion?: boolean;
};

export type ProcedureOption = {
  itemCode?: string;
  itemName?: string;
  itemCategory?: string;
  cardCategory?: string;
  accreditedRate?: number;
  nonAccreditedRate?: number;
};

export const fetchClaimProcedures = async (
  visitId: number,
): Promise<ProcedureOption[]> => {
  const response = await Axios.get(PATIENT_APIS.CLAIM_PROCEDURES(visitId));
  if (response.status === StatusCodes.OK) {
    return response.data ?? [];
  }
  throw new Error("Unable to load procedures");
};

export const fetchSchemeClaimProcedures = async (
  cardCategory?: string | null,
): Promise<ProcedureOption[]> => {
  const response = await Axios.get(PATIENT_APIS.SCHEME_CLAIM_PROCEDURES, {
    params: cardCategory ? { cardCategory } : undefined,
  });
  if (response.status === StatusCodes.OK) {
    return response.data ?? [];
  }
  throw new Error("Unable to load procedures");
};

export const fetchClaim = async (visitId: number): Promise<ClaimResponse> => {
  const response = await Axios.get(PATIENT_APIS.CLAIM(visitId));
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to load claim");
};

export const saveClaimDraft = async (
  visitId: number,
  body: SaveClaimRequest,
  claimId?: number,
): Promise<ClaimResponse> => {
  const response = claimId
    ? await Axios.patch(PATIENT_APIS.CLAIM_BY_ID(visitId, claimId), body)
    : await Axios.post(PATIENT_APIS.CLAIM(visitId), body);
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to save claim draft");
};

export const saveClaim = async (
  visitId: number,
  data: Omit<SaveClaimRequest, "documents"> & { saveAsDraft: boolean },
  files: StagedFile[],
): Promise<ClaimResponse> => {
  const payload: SaveClaimRequest = {
    ...data,
    documents: files.map((f) => ({
      documentType: f.documentType,
      fileName: f.file.name,
    })),
  };
  const response = await Axios.post(
    PATIENT_APIS.CLAIM_SAVE(visitId),
    buildSaveFormData(payload as Record<string, unknown>, files),
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to save claim");
};

export const submitClaim = async (
  visitId: number,
  claimId: number,
): Promise<ClaimResponse> => {
  const response = await Axios.post(PATIENT_APIS.CLAIM_SUBMIT(visitId, claimId));
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to submit claim");
};

export const uploadClaimDocument = async (
  visitId: number,
  claimId: number,
  documentType: string,
  file: File,
): Promise<ClaimResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await Axios.post(
    `${PATIENT_APIS.CLAIM_DOCUMENTS(visitId, claimId)}?documentType=${encodeURIComponent(documentType)}`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to upload claim document");
};

export const claimAction = async (
  visitId: number,
  claimId: number,
  body: StageActionRequest,
): Promise<ClaimResponse> => {
  const response = await Axios.post(PATIENT_APIS.CLAIM_ACTIONS(visitId, claimId), body);
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to apply claim action");
};

export const claimWorkflowAction = async (
  visitId: number,
  claimId: number,
  stage: string,
  body: WorkflowActionRequest,
): Promise<ClaimResponse> => {
  const response = await Axios.post(
    PATIENT_APIS.CLAIM_WORKFLOW(visitId, claimId, stage),
    body,
  );
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to apply workflow action");
};

export const approveSettlement = async (
  visitId: number,
  claimId: number,
): Promise<ClaimResponse> => {
  const response = await Axios.post(
    PATIENT_APIS.CLAIM_SETTLEMENT_APPROVE(visitId, claimId),
  );
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to approve settlement");
};
